import { useEffect, useState } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "@data/firebase";
import { useAuth } from "@contexts/AuthContext";
import { useProjects } from "@contexts/ProjectContext";
import { projectStorageBasePath } from "@domain/project/paths";

/**
 * Attachments on budget rows, scoped to the active project.
 *   Firestore: projects/{pid}/attachments/{autoId}
 *   Storage:   projects/{pid}/attachments/{budgetId}/{timestamp}_{filename}
 */
function attachmentsCollection(project) {
  return collection(db, "projects", project.id, "attachments");
}

function attachmentDocRef(project, id) {
  return doc(db, "projects", project.id, "attachments", id);
}

export default function useAttachments(budgetId) {
  const { user } = useAuth();
  const { activeProject } = useProjects();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !activeProject || budgetId == null) {
      setItems([]);
      setLoading(false);
      return;
    }
    const q = query(
      attachmentsCollection(activeProject),
      where("budgetId", "==", budgetId)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
        setItems(list);
        setLoading(false);
      },
      (err) => {
        console.error("[useAttachments]", err);
        setLoading(false);
      }
    );
    return unsub;
  }, [user, activeProject, budgetId]);

  const upload = async (file) => {
    if (!user || !activeProject || !file) return;
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${projectStorageBasePath(activeProject)}/attachments/${budgetId}/${timestamp}_${safeName}`;
    const sref = storageRef(storage, path);
    await uploadBytes(sref, file);
    const url = await getDownloadURL(sref);
    await addDoc(attachmentsCollection(activeProject), {
      budgetId,
      name: file.name,
      contentType: file.type,
      size: file.size,
      url,
      storagePath: path,
      createdAt: serverTimestamp(),
    });
  };

  const remove = async (attachment) => {
    if (!user || !activeProject || !attachment) return;
    try {
      await deleteObject(storageRef(storage, attachment.storagePath));
    } catch (err) {
      console.warn("[useAttachments] storage delete failed", err);
    }
    await deleteDoc(attachmentDocRef(activeProject, attachment.id));
  };

  return { items, loading, upload, remove };
}
